export interface DateGroup<T> {
  key: string;
  label: string;
  items: T[];
}

export function capitalize(str: string): string {
  return str.length === 0 ? str : str.charAt(0).toUpperCase() + str.slice(1);
}

// Clusters items by actual calendar date, in chronological order -- meant for
// one-off dated items (races, events) where grouping by weekday would mix
// unrelated occurrences (a race in January and one in July aren't related
// just because they both fall on a Saturday).
export function groupByCalendarDay<T>(
  items: T[],
  getDate: (item: T) => Date | null,
  locale: string,
  unscheduledLabel: string
): DateGroup<T>[] {
  const buckets = new Map<string, { date: Date; items: T[] }>();
  const unscheduled: T[] = [];

  items.forEach((item) => {
    const date = getDate(item);
    if (!date) {
      unscheduled.push(item);
      return;
    }
    const dayKey = date.toDateString();
    if (!buckets.has(dayKey)) buckets.set(dayKey, { date, items: [] });
    buckets.get(dayKey)!.items.push(item);
  });

  buckets.forEach((bucket) => {
    bucket.items.sort((a, b) => (getDate(a)?.getTime() ?? 0) - (getDate(b)?.getTime() ?? 0));
  });

  const groups: DateGroup<T>[] = Array.from(buckets.entries())
    .sort(([, a], [, b]) => a.date.getTime() - b.date.getTime())
    .map(([dayKey, bucket]) => ({
      key: dayKey,
      label: capitalize(new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(bucket.date)),
      items: bucket.items,
    }));

  if (unscheduled.length > 0) {
    groups.push({ key: 'unscheduled', label: unscheduledLabel, items: unscheduled });
  }

  return groups;
}

// Clusters items by calendar month, most recent month first -- for the
// history log, where "September 2026 / August 2026 / ..." headers read better
// than one per day. Items keep the order the caller gave them (already sorted
// newest-first). Undated items are dropped.
export function groupByMonth<T>(
  items: T[],
  getDate: (item: T) => Date | null,
  locale: string
): DateGroup<T>[] {
  const buckets = new Map<string, { date: Date; items: T[] }>();

  items.forEach((item) => {
    const date = getDate(item);
    if (!date) return;
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (!buckets.has(key)) {
      buckets.set(key, { date: new Date(date.getFullYear(), date.getMonth(), 1), items: [] });
    }
    buckets.get(key)!.items.push(item);
  });

  return Array.from(buckets.entries())
    .sort(([, a], [, b]) => b.date.getTime() - a.date.getTime())
    .map(([key, bucket]) => ({
      key,
      label: capitalize(new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(bucket.date)),
      items: bucket.items,
    }));
}
